// screens/business/BusinessLoginScreen.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  StatusBar,
  Modal,
  FlatList,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import * as LocalAuthentication from 'expo-local-authentication';
import NetInfo from '@react-native-community/netinfo';
import { useFocusEffect } from '@react-navigation/native';

import { useBusiness } from '../../../context/BusinessContext';
import { useAuth } from '../../../context/AuthContext';
import { storage } from '../../../utils/storage';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const API_HEADERS = {
  'Content-Type': 'application/json',
  'X-API-Key': API_KEY
};

// Helper to generate company code
const generateCompanyCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Safe email validation function
const safeEmailValidation = (email) => {
  try {
    if (!email || email === null || email === undefined) {
      return { isValid: false, error: 'Email is required', value: '' };
    }
    let cleanEmail = '';
    if (typeof email === 'string') {
      cleanEmail = email.trim().toLowerCase();
    } else if (typeof email === 'number') {
      cleanEmail = email.toString().trim().toLowerCase();
    } else if (typeof email === 'object') {
      console.log('⚠️ Email is an object:', email);
      cleanEmail = String(email).trim().toLowerCase();
    } else {
      cleanEmail = String(email).trim().toLowerCase();
    }
    if (!cleanEmail || cleanEmail.length === 0 || cleanEmail === 'undefined' || cleanEmail === 'null') {
      return { isValid: false, error: 'Email is required', value: '' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      return { isValid: false, error: 'Please enter a valid email address', value: cleanEmail };
    }
    return { isValid: true, error: null, value: cleanEmail };
  } catch (error) {
    console.error('❌ Email validation error:', error);
    return { isValid: false, error: 'Invalid email format', value: '' };
  }
};

// Password validation function
const validatePassword = (password) => {
  if (!password || password.trim().length === 0) {
    return { isValid: false, error: 'Password is required' };
  }
  if (password.length < 6) {
    return { isValid: false, error: 'Password must be at least 6 characters' };
  }
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  let warning = null;
  if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
    warning = 'For better security, use uppercase, lowercase letters and numbers';
  }
  return {
    isValid: true,
    warning,
    strength: (hasUpperCase && hasLowerCase && hasNumbers) ? 'strong' : 'weak'
  };
};

// API error handler
const handleApiError = (error) => {
  console.error('API Error:', error);
  if (error.message.includes('Network request failed') || error.message.includes('Failed to fetch')) {
    return { success: false, error: 'Network error. Please check your internet connection and try again.' };
  }
  if (error.message.includes('timeout')) {
    return { success: false, error: 'Request timeout. Please try again.' };
  }
  if (error.message.includes('401')) {
    return { success: false, error: 'Unauthorized. Please check your credentials.' };
  }
  if (error.message.includes('404')) {
    return { success: false, error: 'API endpoint not found. Please contact support.' };
  }
  return { success: false, error: 'Server error. Please try again later.' };
};

// Modal Components
const UserTypeModal = ({ visible, onClose, userTypes, selectedUserType, onSelect }) => (
  <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
    <View style={styles.modalOverlay}>
      <View style={styles.modalContent}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Select User Type</Text>
          <TouchableOpacity onPress={onClose}>
            <MaterialCommunityIcons name="close" size={24} color="#64748b" />
          </TouchableOpacity>
        </View>
        <FlatList
          data={userTypes}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[styles.modalItem, selectedUserType === item.id && styles.modalItemSelected]}
              onPress={() => { onSelect(item.id); onClose(); }}
            >
              <MaterialCommunityIcons
                name={item.icon}
                size={24}
                color={selectedUserType === item.id ? "#0e4e96" : "#64748b"}
              />
              <View style={styles.modalItemContent}>
                <Text style={[styles.modalItemTitle, selectedUserType === item.id && styles.modalItemTitleSelected]}>
                  {item.name}
                </Text>
                <Text style={styles.modalItemDescription}>{item.description}</Text>
              </View>
              {selectedUserType === item.id && (
                <MaterialCommunityIcons name="check" size={20} color="#0e4e96" />
              )}
            </TouchableOpacity>
          )}
        />
      </View>
    </View>
  </Modal>
);

const CompanyModal = ({ visible, onClose, companies, selectedCompany, onSelect }) => (
  <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
    <View style={styles.modalOverlay}>
      <View style={styles.modalContent}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Select Company</Text>
          <TouchableOpacity onPress={onClose}>
            <MaterialCommunityIcons name="close" size={24} color="#64748b" />
          </TouchableOpacity>
        </View>
        <FlatList
          data={companies}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[styles.modalItem, selectedCompany?.id === item.id && styles.modalItemSelected]}
              onPress={() => { onSelect(item); onClose(); }}
            >
              <MaterialCommunityIcons
                name="domain"
                size={24}
                color={selectedCompany?.id === item.id ? "#0e4e96" : "#64748b"}
              />
              <View style={styles.modalItemContent}>
                <Text style={[styles.modalItemTitle, selectedCompany?.id === item.id && styles.modalItemTitleSelected]}>
                  {item.name}
                </Text>
                <Text style={styles.modalItemDescription}>
                  {item.industry} • {item.employees} employees
                </Text>
              </View>
              {selectedCompany?.id === item.id && (
                <MaterialCommunityIcons name="check" size={20} color="#0e4e96" />
              )}
            </TouchableOpacity>
          )}
        />
      </View>
    </View>
  </Modal>
);

const RoleModal = ({ visible, onClose, roles, selectedRole, onSelect }) => (
  <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
    <View style={styles.modalOverlay}>
      <View style={styles.modalContent}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Select Role</Text>
          <TouchableOpacity onPress={onClose}>
            <MaterialCommunityIcons name="close" size={24} color="#64748b" />
          </TouchableOpacity>
        </View>
        <FlatList
          data={roles}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[styles.modalItem, selectedRole?.id === item.id && styles.modalItemSelected]}
              onPress={() => { onSelect(item); onClose(); }}
            >
              <MaterialCommunityIcons
                name="badge-account"
                size={24}
                color={selectedRole?.id === item.id ? "#0e4e96" : "#64748b"}
              />
              <View style={styles.modalItemContent}>
                <Text style={[styles.modalItemTitle, selectedRole?.id === item.id && styles.modalItemTitleSelected]}>
                  {item.name}
                </Text>
                <Text style={styles.modalItemDescription}>
                  {item.department} • {item.level}
                </Text>
              </View>
              {selectedRole?.id === item.id && (
                <MaterialCommunityIcons name="check" size={20} color="#0e4e96" />
              )}
            </TouchableOpacity>
          )}
        />
      </View>
    </View>
  </Modal>
);

export default function BusinessLoginScreen({ navigation }) {
  const { login } = useAuth();
  const { saveUser, saveCompanyProfile, addNotification, ensureUserHasCompany } = useBusiness();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isFocused, setIsFocused] = useState({ email: false, password: false });
  const [isLoading, setIsLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState('');
  const [hasSavedCredentials, setHasSavedCredentials] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [serverStatus, setServerStatus] = useState('unknown');

  // Security states
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTime, setLockTime] = useState(0);

  // Multi-user login states
  const [selectedUserType, setSelectedUserType] = useState('business_owner');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showUserTypeModal, setShowUserTypeModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [recentLogins, setRecentLogins] = useState([]);

  // Company logo state
  const [companyLogo, setCompanyLogo] = useState(null);

  // Multi-user data
  const userTypes = [
    { id: 'business_owner', name: 'Business Owner', icon: 'account-tie', description: 'Full access to all business features' },
    { id: 'manager', name: 'Manager', icon: 'account-supervisor', description: 'Manage teams and projects' },
    { id: 'employee', name: 'Employee', icon: 'account', description: 'Access assigned projects and tasks' },
    { id: 'contractor', name: 'Contractor', icon: 'account-hard-hat', description: 'External contractor access' },
    { id: 'client', name: 'Client', icon: 'account-group', description: 'Client portal access' },
  ];

  const companies = [
    { id: 'jobeng_ltd', name: 'JobEng Ltd', industry: 'Construction', employees: 150 },
    { id: 'buildtech_solutions', name: 'BuildTech Solutions', industry: 'Engineering', employees: 85 },
    { id: 'premier_construction', name: 'Premier Construction', industry: 'Construction', employees: 220 },
    { id: 'innovative_designs', name: 'Innovative Designs', industry: 'Architecture', employees: 45 },
    { id: 'tech_builders', name: 'Tech Builders', industry: 'Technology', employees: 120 },
  ];

  const roles = [
    { id: 'ceo', name: 'Chief Executive Officer', department: 'Executive', level: 'C-Level' },
    { id: 'project_manager', name: 'Project Manager', department: 'Operations', level: 'Management' },
    { id: 'site_supervisor', name: 'Site Supervisor', department: 'Construction', level: 'Supervisor' },
    { id: 'engineer', name: 'Senior Engineer', department: 'Engineering', level: 'Senior' },
    { id: 'architect', name: 'Lead Architect', department: 'Design', level: 'Lead' },
    { id: 'hr_manager', name: 'HR Manager', department: 'Human Resources', level: 'Management' },
    { id: 'finance_director', name: 'Finance Director', department: 'Finance', level: 'Director' },
    { id: 'safety_officer', name: 'Safety Officer', department: 'Safety', level: 'Officer' },
  ];

  const selectedUserTypeData = useMemo(() =>
    userTypes.find(type => type.id === selectedUserType),
    [selectedUserType]
  );

  // ==================== LOAD COMPANY LOGO ====================
  useFocusEffect(
    useCallback(() => {
      loadCompanyLogo();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await storage.getObject('company_profile');
      if (cachedProfile) {
        setCompanyLogo(cachedProfile.logo || null);
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Test server connection
  const testServerConnection = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'HEAD',
        signal: controller.signal,
        headers: API_HEADERS
      });
      clearTimeout(timeoutId);
      setServerStatus(response.ok ? 'connected' : 'error');
    } catch {
      setServerStatus('error');
    }
  };

  useEffect(() => {
    initializeScreen();
    testServerConnection();

    const unsubscribe = NetInfo.addEventListener(state => {
      setIsConnected(state.isConnected);
    });

    return () => unsubscribe();
  }, []);

  const initializeScreen = async () => {
    try {
      await loadSavedData();
      await checkBiometricAvailability();
      await loadSecuritySettings();
    } catch (error) {
      console.error('Error initializing screen:', error);
    }
  };

  const loadSecuritySettings = async () => {
    try {
      const attempts = await storage.getItem('login_attempts');
      const locked = await storage.getItem('account_locked');
      const lockTimeStr = await storage.getItem('lock_time');

      if (attempts) setLoginAttempts(parseInt(attempts));
      if (locked === 'true') {
        setIsLocked(true);
        const currentTime = Date.now();
        const lockEndTime = parseInt(lockTimeStr);
        if (currentTime < lockEndTime) {
          setLockTime(lockEndTime);
          setTimeout(() => {
            setIsLocked(false);
            setLoginAttempts(0);
            storage.multiRemove(['account_locked', 'lock_time', 'login_attempts']);
          }, lockEndTime - currentTime);
        } else {
          setIsLocked(false);
          storage.multiRemove(['account_locked', 'lock_time', 'login_attempts']);
        }
      }
    } catch (error) {
      console.error('Error loading security settings:', error);
    }
  };

  const checkBiometricAvailability = async () => {
    try {
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      const supportedTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();

      const available = hasHardware && isEnrolled;
      setBiometricAvailable(available);

      if (available && supportedTypes.length > 0) {
        if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
          setBiometricType('Face ID');
        } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
          setBiometricType('Touch ID');
        } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.IRIS)) {
          setBiometricType('Iris');
        } else {
          setBiometricType('Biometric');
        }
      }

      const savedCredentials = await storage.getObject('biometric_credentials');
      setHasSavedCredentials(!!savedCredentials);

      console.log('✅ Biometric availability checked:', { available, type: biometricType, hasSaved: !!savedCredentials });
    } catch (error) {
      console.error('❌ Error checking biometric availability:', error);
      setBiometricAvailable(false);
    }
  };

  const loadSavedData = async () => {
    try {
      const savedEmail = await storage.getItem('saved_business_email');
      const rememberLogin = await storage.getItem('remember_business_login');
      const recentLoginsData = await storage.getObject('recent_business_logins');

      if (savedEmail && rememberLogin === 'true') {
        setEmail(savedEmail);
        setRememberMe(true);
      }

      if (recentLoginsData && Array.isArray(recentLoginsData)) {
        setRecentLogins(recentLoginsData);
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
  };

  const saveLoginPreferences = async (emailValue) => {
    try {
      const safeEmail = emailValue.trim().toLowerCase();
      if (!safeEmail) return;
      if (rememberMe) {
        await storage.setItem('saved_business_email', safeEmail);
        await storage.setItem('remember_business_login', 'true');
      } else {
        await storage.removeItem('saved_business_email');
        await storage.removeItem('remember_business_login');
      }
    } catch (error) {
      console.error('Error saving login preferences:', error);
    }
  };

  const getDashboardRoute = (userType) => {
    switch (userType) {
      case 'business_owner': return 'WelcomeBusiness';
      case 'manager': return 'ManagerDashboard';
      case 'employee': return 'EmployeeDashboard';
      default: return 'CompanyProfileScreen';
    }
  };

  const handleFailedAttempt = async () => {
    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);
    await storage.setItem('login_attempts', String(newAttempts));
    if (newAttempts >= 5) {
      const lockDuration = 15 * 60 * 1000;
      const lockEndTime = Date.now() + lockDuration;
      setIsLocked(true);
      setLockTime(lockEndTime);
      await storage.setItem('account_locked', 'true');
      await storage.setItem('lock_time', String(lockEndTime));
      setTimeout(() => {
        setIsLocked(false);
        setLoginAttempts(0);
        storage.multiRemove(['account_locked', 'lock_time', 'login_attempts']);
      }, lockDuration);
      Alert.alert(
        'Too Many Attempts',
        'Account temporarily locked for 15 minutes due to multiple failed login attempts.'
      );
    }
  };

  const resetSecuritySettings = async () => {
    setLoginAttempts(0);
    setIsLocked(false);
    await storage.multiRemove(['account_locked', 'lock_time', 'login_attempts']);
  };

  // ==================== FETCH COMPANY PROFILE ====================
  const fetchCompanyProfile = async (companyId, token) => {
    try {
      // Use correct endpoint /api/company-profile/:id
      const response = await fetch(`${API_BASE_URL}/api/company-profile/${companyId}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${token}`,
        }
      });
      if (!response.ok) {
        console.warn('[Login] fetchCompanyProfile HTTP:', response.status);
        return null;
      }
      const result = await response.json();
      const company = result.data || result;
      if (!company?.id) return null;

      // Full field mapping — all fields used by app screens
      const profile = {
        id:                 String(company.id),
        name:               company.company_name  || company.name          || '',
        email:              company.email          || '',
        phone:              company.phone          || '',
        website:            company.website        || '',
        industry:           company.industry       || '',
        companySize:        company.employee_count ? String(company.employee_count) : '',
        logo:               company.logo           || company.logo_url
                          || company.company_logo  || company.banner_image_url || null,
        headquarters:       company.address        || '',
        description:        company.description    || '',
        vatNumber:          company.vat_number     || '',
        registrationNumber: company.registration_number || '',
        companyCode:        company.company_code   || '',
        companyType:        company.company_type   || '',
        founded:            company.founded        ? String(company.founded) : '',
        linkedIn:           company.linkedin_url   || '',
        user_id:            String(company.user_id || ''),
        // Invoice settings
        paymentTerms:       String(company.payment_terms    || 30),
        defaultTaxRate:     String(company.default_tax_rate || 20),
        invoicePrefix:      company.invoice_prefix || 'INV',
      };

      console.log('[Login] Company profile fetched:', profile.name, '(id:', profile.id + ')');
      return profile;
    } catch (error) {
      console.error('[Login] Error fetching company profile:', error.message);
      return null;
    }
  };

  // ==================== ENHANCED LOGIN HANDLER ====================
  const handleLogin = async () => {
    // Clear stale per-company cache BEFORE login to prevent leaking
    // another company's logo/profile into this session.
    try {
      await AsyncStorage.multiRemove([
        // Identity — critical, these are read by many screens
        'user_id','userId','professional_user_id','business_user_id',
        // Tokens
        'auth_token','userToken','token','refresh_token',
        // Company-scoped
        'company_profile','company_logo','company_id','companyProfile','companyLogo',
        // Business-scoped subsystems
        'business_subscription','payroll_employees','hr_employees','hired_employees',
        'erp_favorites','erp_recent_searches','campaigns_cache',
        'business_user_profile','businessUserProfile',
        // Professional-scoped residue (if switching from professional)
        'prof_user_profile','userProfile','professional_default_userProfile',
        'profileStats','prof_dashboard_data',
        // Session bundles
        'userData','user','currentUser',
      ]);
    } catch {}

    if (!isConnected) {
      Alert.alert('Offline', 'Please check your internet connection and try again.');
      return;
    }
    if (isLocked) {
      const remainingTime = Math.ceil((lockTime - Date.now()) / 1000 / 60);
      Alert.alert(
        'Account Temporarily Locked',
        `Too many failed attempts. Please try again in ${remainingTime} minutes.`
      );
      return;
    }

    const emailValidation = safeEmailValidation(email);
    if (!emailValidation.isValid) {
      Alert.alert('Email Error', emailValidation.error);
      return;
    }
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      Alert.alert('Password Error', passwordValidation.error);
      return;
    }

    setIsLoading(true);

    try {
      const loginData = {
        email: emailValidation.value,
        password: password.trim(),
        user_type: selectedUserType,
        company_id: selectedCompany?.id || null,
        role: selectedRole?.id || null,
        remember_me: rememberMe,
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify(loginData),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        if (!result.data || !result.data.token || !result.data.user) {
          throw new Error('Invalid response data from server');
        }

        const user = result.data.user;
        const token = result.data.token;

        if (user.is_active === false) {
          Alert.alert('Account Inactive', 'Your account is not active. Please contact support.');
          return;
        }
        if (user.is_verified === false && user.verification_required !== false) {
          Alert.alert('Email Not Verified', 'Please verify your email address before logging in.');
          navigation.navigate('EmailVerification', { email: user.email });
          return;
        }

        // ✅ 2. Save user data to storage — ensure role fields are included
        const enrichedUser = {
          ...user,
          user_role: user.user_role || user.role || selectedUserType || 'business',
          user_type: user.user_type || selectedUserType || 'business',
          business_role: user.business_role || selectedRole?.id || '',
        };

        // ✅ 0. Clear all stale session keys before storing new session
        const SESSION_KEYS = [
          'user_id', 'user_email', 'user_name', 'auth_token', 'token',
          'company_id', 'business_id', 'employee_id',
          'user_data', 'company_profile', 'business_user_data_7',
          'employee_dashboard_cache', 'payroll_ext_12', 'my_payroll_12',
          'my_payslips_12', 'employee_id', 'professional_id',
        ];
        await Promise.all(SESSION_KEYS.map(k => storage.removeItem(k).catch(() => {})));

        // ✅ 1. Save enriched user to context
        await saveUser(enrichedUser);
        await storage.setItem('user_id',      String(user.id));
        await storage.setItem('user_email',   user.email);
        await storage.setItem('user_name',    user.full_name || user.name || '');
        await storage.setItem('auth_token',   token);
        await storage.setObject('user_data',  enrichedUser);
        // Store canonical employee_id from employees table (not users.id)
        if (user.employee_id) {
          await storage.setItem('employee_id', String(user.employee_id));
          await storage.setItem('professional_id', String(user.employee_id));
        }

        // ✅ 3. Handle company profile
        let companyProfile = null;
        let companyId = user.company_id;

        // First try to get company from user object
        if (user.company_profile) {
          companyProfile = user.company_profile;
          companyId = companyProfile.id;
        } 
        // Then try to fetch by company_id
        else if (companyId) {
          companyProfile = await fetchCompanyProfile(companyId, token);
        }

        // If still no company and user is business_owner, create one
        if (!companyProfile && selectedUserType === 'business_owner') {
          console.log('🆕 No company found for business owner, creating one');
          
          const newCompanyData = {
            company_name: user.full_name ? `${user.full_name.split(' ')[0]}'s Company` : 'My Company',
            email: user.email || '',
            industry: '',
            employee_count: 1,
            phone: '',
            website: '',
            logo: null,
            address: '',
            description: '',
            vat_number: '',
            registration_number: '',
            company_code: generateCompanyCode(),
            user_id: user.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          const createResponse = await fetch(`${API_BASE_URL}/api/company-profile`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-API-Key': API_KEY,
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(newCompanyData),
          });

          const createResult = await createResponse.json();
          if (createResult.success && createResult.data) {
            const company = createResult.data;
            companyProfile = {
              id: String(company.id),
              name: company.company_name || '',
              email: company.email || '',
              industry: company.industry || '',
              companySize: company.employee_count ? String(company.employee_count) : '',
              logo: company.logo || null,
              companyCode: company.company_code || '',
              user_id: String(company.user_id),
            };
            companyId = company.id;
          }
        }

        // ✅ 4. Save company profile if we have it
        if (companyProfile) {
          await saveCompanyProfile(companyProfile);
          // Save to ALL keys used across the app
          await storage.setItem('company_id',    String(companyProfile.id));
          await storage.setItem('business_id',   String(companyProfile.id));
          await storage.setObject('company_profile', companyProfile);
          setCompanyLogo(companyProfile.logo);
          console.log('✅ Login successful');
          console.log('[Login] Company profile saved:', companyProfile.name, '(id:', companyProfile.id + ')');
        } else {
          console.warn('⚠️ No company profile available for this user');
          
          if (selectedUserType === 'business_owner') {
            Alert.alert(
              'Company Data Missing',
              'Your company profile could not be loaded. You can still continue but may need to set up your company details.',
              [
                { text: 'Continue Anyway', style: 'cancel' },
                { text: 'Retry', onPress: () => handleLogin() }
              ]
            );
          }
        }

        // ✅ 5. Save login preferences
        await saveLoginPreferences(user.email || emailValidation.value);

        // ✅ 6. Save biometric credentials if enabled
        if (biometricAvailable && rememberMe) {
          await saveBiometricCredentials(token, user.id);
        }

        // ✅ 7. Reset security settings
        await resetSecuritySettings();

        // ✅ 8. Update recent logins
        const loginEntry = {
          email: user.email,
          user_type: selectedUserType,
          company: selectedCompany,
          role: selectedRole,
          timestamp: new Date().toISOString(),
        };
        const updatedRecentLogins = [loginEntry, ...recentLogins.filter(l => l.email !== user.email)].slice(0, 5);
        setRecentLogins(updatedRecentLogins);
        await storage.setObject('recent_business_logins', updatedRecentLogins);

        // ✅ 9. Welcome notification
        await addNotification({
          title: 'Welcome back!',
          message: `Logged in as ${user.full_name || user.email}`,
          type: 'info',
          priority: 'low',
        });

        // ✅ 10. Final sync — subscription only (company already saved above)
        try {
          // Sync subscription
          const subRes = await fetch(
            `${API_BASE_URL}/api/business-subscriptions?company_id=${companyProfile?.id || user.company_id}&status=active`,
            { headers: { 'X-API-Key': API_KEY, 'Authorization': `Bearer ${token}` } }
          );
          if (subRes.ok) {
            const subData = await subRes.json();
            const sub = Array.isArray(subData.data) ? subData.data[0] : null;
            if (sub) {
              const normSub = {
                id:            sub.id,
                plan_id:       sub.plan_id,
                plan_name:     sub.plan_name || sub.name || sub.plan_id,
                price:         parseFloat(sub.price || sub.price_paid || 0),
                billing_cycle: sub.billing_cycle || 'monthly',
                max_users:     sub.max_users || sub.users_count || 1,
                features:      Array.isArray(sub.features) ? sub.features : [],
                status:        sub.status || 'active',
                company_id:   companyProfile?.id,
              };
              await storage.setObject('business_subscription', normSub);
              console.log('[Login] Subscription synced:', normSub.plan_name, '£'+normSub.price+'/mo');
            }
          }
        } catch (syncErr) {
          console.warn('[Login] Post-login sync failed (non-fatal):', syncErr.message);
        }

        // ✅ 11. Navigate to appropriate screen
        const dashboardRoute = getDashboardRoute(selectedUserType);

        if (companyProfile) {
          navigation.replace(dashboardRoute, {
            companyProfile: companyProfile,
            fromLogin: true,
          });
        } else {
          navigation.replace(dashboardRoute);
        }

      } else {
        await handleFailedAttempt();
        let errorMessage = 'Invalid credentials. Please try again.';
        let showSignUpOption = false;
        switch (result.error) {
          case 'USER_NOT_FOUND':
            errorMessage = 'No account found with this email address.';
            showSignUpOption = true;
            break;
          case 'INVALID_PASSWORD':
            errorMessage = 'Incorrect password. Please try again.';
            break;
          case 'ACCOUNT_NOT_VERIFIED':
            errorMessage = 'Please verify your email address before logging in.';
            break;
          case 'ACCOUNT_SUSPENDED':
            errorMessage = 'Your account has been suspended. Please contact support.';
            break;
          case 'INVALID_USER_TYPE':
            errorMessage = 'This account type cannot access the business portal.';
            break;
          default:
            errorMessage = result.error || 'Invalid credentials. Please try again.';
        }
        if (showSignUpOption) {
          Alert.alert(
            'Account Not Found',
            errorMessage,
            [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Sign Up', onPress: () => navigation.navigate('BusinessSignup', { email: emailValidation.value }) }
            ]
          );
        } else {
          Alert.alert('Login Failed', errorMessage);
        }
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      if (error.name === 'AbortError') {
        Alert.alert('Timeout', 'Request timed out. Please try again.');
      } else {
        const apiError = handleApiError(error);
        Alert.alert('Login Error', apiError.error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    try {
      setIsLoading(true);
      if (!biometricAvailable) {
        Alert.alert('Biometric Login', 'Biometric authentication is not available on this device');
        return;
      }
      const savedCredentials = await storage.getObject('biometric_credentials');
      if (!savedCredentials) {
        Alert.alert('Biometric Login', 'No saved biometric credentials found. Please login with email/password first and enable "Remember Me".');
        return;
      }

      const biometricResult = await LocalAuthentication.authenticateAsync({
        promptMessage: 'Authenticate to login to JobEng Business',
        cancelLabel: 'Cancel',
        fallbackLabel: 'Use Password',
        disableDeviceFallback: false,
      });

      if (biometricResult.success) {
        const credentials = savedCredentials;
        
        // Attempt server validation
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          const response = await fetch(`${API_BASE_URL}/api/auth/biometric-login`, {
            method: 'POST',
            headers: API_HEADERS,
            body: JSON.stringify({
              biometric_token: credentials.token,
              user_id: credentials.user_id,
              device_id: credentials.device_id || 'unknown',
              user_type: selectedUserType,
              company_id: selectedCompany?.id,
              role: selectedRole?.id
            }),
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          const result = await response.json();
          
          if (result.success) {
            await storage.setItem('auth_token', result.data.token);
            await saveUser(result.data.user);
            
            // Handle company profile
            if (result.data.user.company_profile) {
              await saveCompanyProfile(result.data.user.company_profile);
              await storage.setObject('company_profile', result.data.user.company_profile);
            }
            
            await resetSecuritySettings();
            await addNotification({
              title: 'Welcome back!',
              message: `Logged in as ${result.data.user.full_name || result.data.user.email}`,
              type: 'info',
              priority: 'low'
            });
            console.log('✅ Biometric login successful');
            
            const dashboardRoute = getDashboardRoute(selectedUserType);
            if (result.data.user.company_profile) {
              navigation.replace(dashboardRoute, { 
                companyProfile: result.data.user.company_profile,
                fromLogin: true 
              });
            } else {
              navigation.replace(dashboardRoute);
            }
            return;
          }
        } catch (serverError) {
          console.warn('Server biometric validation failed, using fallback:', serverError);
        }

        // Fallback: use stored credentials
        if (credentials.email) {
          const fallbackUser = {
            id: credentials.user_id,
            email: credentials.email,
            name: credentials.name || 'Biometric User',
            userType: selectedUserType,
            company: selectedCompany,
            role: selectedRole,
            loginMethod: 'biometric'
          };
          await storage.setItem('auth_token', credentials.token);
          await saveUser(fallbackUser);
          await resetSecuritySettings();
          await addNotification({
            title: 'Welcome back!',
            message: `Logged in as ${fallbackUser.email}`,
            type: 'info',
            priority: 'low'
          });
          console.log('✅ Biometric fallback login successful');
          navigation.replace(getDashboardRoute(selectedUserType));
        } else {
          throw new Error('Invalid biometric credentials');
        }
      } else {
        if (biometricResult.error && biometricResult.error !== 'UserCancel') {
          Alert.alert('Biometric Authentication Failed', biometricResult.error);
        }
      }
    } catch (error) {
      console.error('❌ Biometric login error:', error);
      Alert.alert('Biometric Login Error', 'Unable to login with biometrics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const saveBiometricCredentials = async (userToken, userId) => {
    try {
      if (biometricAvailable && rememberMe) {
        const biometricCredentials = {
          token: userToken,
          user_id: String(userId),
          email: email,
          name: 'Business User',
          device_id: 'device_' + Date.now(),
          created_at: new Date().toISOString()
        };
        await storage.setObject('biometric_credentials', biometricCredentials);
        setHasSavedCredentials(true);
        console.log('✅ Biometric credentials saved');
      }
    } catch (error) {
      console.error('❌ Error saving biometric credentials:', error);
    }
  };

  const handleBack = () => {
    navigation.goBack();
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContainer}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Header Gradient with Company Logo */}
          <LinearGradient colors={['#0e4e96', '#2670ba']} style={styles.headerGradient}>
            <View style={styles.headerContainer}>
              <TouchableOpacity style={styles.backButton} onPress={handleBack}>
                <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
                <Text style={styles.backButtonText}>Back</Text>
              </TouchableOpacity>

              {/* Logo – company if available, otherwise default JobEng icon */}
              <View style={styles.logoWrapper}>
                {companyLogo ? (
                  <Image source={{ uri: companyLogo || undefined }} style={styles.logo} resizeMode="contain" />
                ) : (
                  <MaterialCommunityIcons name="office-building" size={60} color="#fff" />
                )}
              </View>

              <Text style={styles.title}>JobEng Business</Text>
              <Text style={styles.subtitle}>UK Engineering Excellence</Text>

              {/* Connection Status */}
              <View style={styles.connectionStatusContainer}>
                <Ionicons
                  name={serverStatus === 'connected' ? "cloud-done" : serverStatus === 'error' ? "cloud-offline" : "cloud-outline"}
                  size={16}
                  color={serverStatus === 'connected' ? "#4CAF50" : serverStatus === 'error' ? "#F44336" : "#FFC107"}
                />
                <Text style={styles.connectionStatusText}>
                  <Text>{serverStatus === 'connected' ? 'Connected' : serverStatus === 'error' ? 'Connection Error' : 'Checking...'}</Text>
                </Text>
              </View>

              <TouchableOpacity style={styles.testConnectionButton} onPress={testServerConnection}>
                <Text style={styles.testConnectionText}>Test Server Connection</Text>
              </TouchableOpacity>
            </View>
          </LinearGradient>

          {/* White Form Container */}
          <View style={styles.formContainer}>
            {!isConnected && (
              <View style={styles.offlineBanner}>
                <Ionicons name="cloud-offline" size={16} color="#FFFFFF" />
                <Text style={styles.offlineText}>You are offline</Text>
              </View>
            )}

            {/* User Type Selection */}
            <TouchableOpacity style={styles.selectorButton} onPress={() => setShowUserTypeModal(true)}>
              <View style={styles.selectorContent}>
                <MaterialCommunityIcons name={selectedUserTypeData?.icon || 'account'} size={24} color="#0e4e96" />
                <View style={styles.selectorTextContainer}>
                  <Text style={styles.selectorTitle}>{selectedUserTypeData?.name || 'Select User Type'}</Text>
                  <Text style={styles.selectorDescription}>{selectedUserTypeData?.description || 'Choose your role'}</Text>
                </View>
              </View>
              <MaterialCommunityIcons name="chevron-down" size={24} color="#64748B" />
            </TouchableOpacity>

            {/* Company Selection (for certain types) */}
            {['manager', 'employee', 'contractor'].includes(selectedUserType) && (
              <TouchableOpacity style={styles.selectorButton} onPress={() => setShowCompanyModal(true)}>
                <View style={styles.selectorContent}>
                  <MaterialCommunityIcons name="domain" size={24} color="#0e4e96" />
                  <View style={styles.selectorTextContainer}>
                    <Text style={styles.selectorTitle}>{selectedCompany?.name || 'Select Company'}</Text>
                    <Text style={styles.selectorDescription}>{selectedCompany?.industry || 'Choose your company'}</Text>
                  </View>
                </View>
                <MaterialCommunityIcons name="chevron-down" size={24} color="#64748B" />
              </TouchableOpacity>
            )}

            {/* Role Selection (for certain types) */}
            {['manager', 'employee'].includes(selectedUserType) && (
              <TouchableOpacity style={styles.selectorButton} onPress={() => setShowRoleModal(true)}>
                <View style={styles.selectorContent}>
                  <MaterialCommunityIcons name="badge-account" size={24} color="#0e4e96" />
                  <View style={styles.selectorTextContainer}>
                    <Text style={styles.selectorTitle}>{selectedRole?.name || 'Select Role'}</Text>
                    <Text style={styles.selectorDescription}>{selectedRole?.department || 'Choose your role'}</Text>
                  </View>
                </View>
                <MaterialCommunityIcons name="chevron-down" size={24} color="#64748B" />
              </TouchableOpacity>
            )}

            {/* Email Input */}
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Email Address</Text>
              <View style={[styles.inputWrapper, isFocused.email && styles.inputWrapperFocused]}>
                <Ionicons name="mail-outline" size={20} color={isFocused.email ? "#0e4e96" : "#64748B"} />
                <TextInput
                  style={styles.input}
                  placeholder="Enter your email"
                  placeholderTextColor="#94A3B8"
                  value={email}
                  onChangeText={setEmail}
                  onFocus={() => setIsFocused(prev => ({ ...prev, email: true }))}
                  onBlur={() => setIsFocused(prev => ({ ...prev, email: false }))}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  editable={!isLoading}
                />
              </View>
            </View>

            {/* Password Input */}
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Password</Text>
              <View style={[styles.inputWrapper, isFocused.password && styles.inputWrapperFocused]}>
                <Ionicons name="lock-closed-outline" size={20} color={isFocused.password ? "#0e4e96" : "#64748B"} />
                <TextInput
                  style={styles.input}
                  placeholder="Enter password (min 6 characters)"
                  placeholderTextColor="#94A3B8"
                  value={password}
                  onChangeText={setPassword}
                  onFocus={() => setIsFocused(prev => ({ ...prev, password: true }))}
                  onBlur={() => setIsFocused(prev => ({ ...prev, password: false }))}
                  secureTextEntry={!showPassword}
                  editable={!isLoading}
                />
                <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                  <Ionicons name={showPassword ? "eye-off" : "eye"} size={20} color="#64748B" />
                </TouchableOpacity>
              </View>
            </View>

            {/* Remember Me & Forgot Password */}
            <View style={styles.rememberMeContainer}>
              <TouchableOpacity style={styles.rememberMeButton} onPress={() => setRememberMe(!rememberMe)}>
                <Ionicons name={rememberMe ? "checkbox" : "square-outline"} size={20} color="#0e4e96" />
                <Text style={styles.rememberMeText}>Remember Me</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => navigation.navigate('ForgotPassword', { userType: 'business' })}>
                <Text style={styles.forgotPasswordText}>Forgot Password?</Text>
              </TouchableOpacity>
            </View>

            {/* Login Button */}
            <TouchableOpacity
              style={[styles.loginButton, (isLoading || isLocked || !isConnected) && styles.loginButtonDisabled]}
              onPress={handleLogin}
              disabled={isLoading || isLocked || !isConnected}
            >
              {isLoading ? (
                <ActivityIndicator size={20} color="#FFFFFF" />
              ) : (
                <>
                  <Text style={styles.loginButtonText}>Login</Text>
                  <Ionicons name="arrow-forward" size={20} color="#FFFFFF" />
                </>
              )}
            </TouchableOpacity>

            {/* Biometric Login */}
            {biometricAvailable && hasSavedCredentials && (
              <TouchableOpacity
                style={styles.biometricButton}
                onPress={handleBiometricLogin}
                disabled={isLoading || isLocked || !isConnected}
              >
                <MaterialCommunityIcons
                  name={biometricType === 'Face ID' ? "face-recognition" : "fingerprint"}
                  size={24}
                  color="#0e4e96"
                />
                <Text style={styles.biometricButtonText}>Login with {biometricType}</Text>
              </TouchableOpacity>
            )}

            {/* Recent Logins */}
            {recentLogins.length > 0 && (
              <TouchableOpacity style={styles.recentLoginsButton} onPress={() => setShowRecentLogins(true)}>
                <MaterialCommunityIcons name="history" size={20} color="#0e4e96" />
                <Text style={styles.recentLoginsButtonText}>Recent Logins</Text>
              </TouchableOpacity>
            )}

            {/* Footer Links */}
            <View style={styles.footer}>
              <Text style={styles.footerText}>
                Don't have an account?{' '}
                <Text style={styles.footerLink} onPress={() => navigation.navigate('BusinessSignup')}>
                  Sign Up
                </Text>
              </Text>
              <TouchableOpacity style={styles.switchModeButton} onPress={() => navigation.navigate('UserTypeSelection')}>
                <MaterialCommunityIcons name="swap-horizontal" size={16} color="#0e4e96" />
                <Text style={styles.switchModeText}>Switch to Professional/Learning</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Modals */}
      <UserTypeModal
        visible={showUserTypeModal}
        onClose={() => setShowUserTypeModal(false)}
        userTypes={userTypes}
        selectedUserType={selectedUserType}
        onSelect={setSelectedUserType}
      />
      <CompanyModal
        visible={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        companies={companies}
        selectedCompany={selectedCompany}
        onSelect={setSelectedCompany}
      />
      <RoleModal
        visible={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        roles={roles}
        selectedRole={selectedRole}
        onSelect={setSelectedRole}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  headerGradient: {
    paddingTop: 20,
    paddingBottom: 30,
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  headerContainer: {
    width: '100%',
    alignItems: 'center',
  },
  backButton: {
    position: 'absolute',
    left: 0,
    top: 0,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: '#FFFFFF',
    marginLeft: 4,
    fontWeight: '500',
  },
  logoWrapper: {
    marginBottom: 12,
  },
  logo: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
    textAlign: 'center',
    marginBottom: 10,
  },
  connectionStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
  },
  connectionStatusText: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    marginLeft: 4,
  },
  testConnectionButton: {
    marginTop: 10,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 16,
  },
  testConnectionText: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '500',
  },
  formContainer: {
    backgroundColor: '#F8FAFC',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    marginTop: -20,
    padding: 24,
    flex: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  offlineBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F44336',
    padding: 10,
    borderRadius: 8,
    marginBottom: 16,
  },
  offlineText: {
    color: '#FFFFFF',
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
  },
  selectorButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  selectorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  selectorTextContainer: {
    marginLeft: 12,
    flex: 1,
  },
  selectorTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  selectorDescription: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 2,
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 6,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 2,
    borderColor: '#E2E8F0',
  },
  inputWrapperFocused: {
    borderColor: '#0e4e96',
    backgroundColor: '#FFFFFF',
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#0F172A',
    marginLeft: 8,
    padding: 0,
  },
  rememberMeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  rememberMeButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rememberMeText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#64748B',
  },
  forgotPasswordText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '600',
  },
  loginButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 12,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#0e4e96',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
    marginBottom: 16,
  },
  loginButtonDisabled: {
    backgroundColor: '#94A3B8',
    shadowOpacity: 0,
    elevation: 0,
  },
  loginButtonText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginRight: 8,
  },
  biometricButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#0e4e96',
    marginBottom: 24,
  },
  biometricButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0e4e96',
    marginLeft: 8,
  },
  recentLoginsButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    marginBottom: 24,
  },
  recentLoginsButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0e4e96',
    marginLeft: 8,
  },
  footer: {
    alignItems: 'center',
  },
  footerText: {
    fontSize: 16,
    color: '#64748B',
    marginBottom: 16,
  },
  footerLink: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  switchModeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  switchModeText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '500',
    marginLeft: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  modalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  modalItemSelected: {
    backgroundColor: '#F0F9FF',
  },
  modalItemContent: {
    marginLeft: 12,
    flex: 1,
  },
  modalItemTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#0F172A',
  },
  modalItemTitleSelected: {
    color: '#0e4e96',
  },
  modalItemDescription: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 2,
  },
});
